package dev.kuml.portal.web.routing

import io.kvision.navigo.Handler
import io.kvision.routing.Routing

/** The three flat screens (design spec: "flache, dreistufige Hierarchie ohne verschachtelte
 * Menues -- Auth -> Projekt-Liste -> Editor") plus the three static, unauthenticated legal pages
 * (§ 5 TMG / Art. 13 DSGVO / AGB) reachable from every screen via [dev.kuml.portal.web.components.legalLinks]. */
public sealed interface Screen {
    public data object Login : Screen

    /** R-3: `/verify-email/:token` -- the landing page `EmailService.sendEmailVerification`'s link
     * actually points at. Unauthenticated, exactly like [Login]. */
    public data class VerifyEmail(
        val token: String,
    ) : Screen

    /** R-4: `/reset-password/:token` -- the landing page `EmailService.sendPasswordReset`'s link
     * actually points at. Unauthenticated, exactly like [Login]. */
    public data class ResetPassword(
        val token: String,
    ) : Screen

    public data object ProjectList : Screen

    /** R-22: [diagramId] is `null` on a bare `/projects/:id` deep link (no diagram segment) --
     * [dev.kuml.portal.web.PortalApp.renderScreen] resolves it to the project's
     * [dev.kuml.portal.web.api.ProjectResponse.defaultDiagramId] and replaces the route before
     * mounting the editor, so the editor screen itself always receives a concrete diagram id. */
    public data class Editor(
        val projectId: String,
        val diagramId: String? = null,
    ) : Screen

    /** R-16: account profile (avatar upload/removal). Reachable from the header avatar. */
    public data object Profile : Screen

    public data object Impressum : Screen

    public data object Datenschutz : Screen

    public data object Agb : Screen

    /** R-21: the public, unauthenticated share-link viewer (`/s/:token`) -- see
     * `dev.kuml.portal.web.share.publicShareScreen`'s KDoc for why it carries no
     * `dev.kuml.portal.web.components.portalHeader`. */
    public data class PublicShare(
        val token: String,
    ) : Screen
}

/**
 * Client-side history routing for `/login`, `/projects`, `/projects/:id` (Navigo 8+ via
 * `kvision-routing-navigo-ng`, `useHash = false`). `root = "/"` is passed EXPLICITLY rather than
 * left to the library's auto-detected default: `Routing`'s no-arg-root behaviour resolves the root
 * from `window.location.pathname` AT CONSTRUCTION TIME when `useHash = false` -- on a deep-link
 * reload of e.g. `/projects/abc-123` that would wrongly compute the *current full path* as the
 * router's root instead of `/`, breaking route matching for exactly the reload case the design spec
 * requires to work. An explicit `root = "/"` sidesteps that entirely.
 *
 * Each route handler is bound as an explicitly-typed `Handler` value (not an inline trailing
 * lambda) and passed as a plain positional argument -- `Navigo.on` has several overloads sharing
 * the same arity, and giving the compiler an already-typed `Handler` removes any ambiguity about
 * which one is meant, rather than relying on trailing-lambda target-type inference to sort it out.
 *
 * @param onScreen invoked with the resolved [Screen] every time the route changes (including the
 *   very first resolution, done automatically by `startApplication` right after [PortalApp.start]).
 */
public class Router(
    private val onScreen: (Screen) -> Unit,
) {
    private val routing: Routing = Routing.init(root = "/", useHash = false)

    init {
        val loginHandler: Handler = { onScreen(Screen.Login) }
        // R-3/R-4: the two unauthenticated, token-in-path landing routes a clicked email link
        // resolves to (see `EmailService.link` server-side) -- registered like `publicShareHandler`
        // below, BEFORE notFound().
        val verifyEmailHandler: Handler = { match ->
            val token = match.data.token as String
            onScreen(Screen.VerifyEmail(token))
        }
        val resetPasswordHandler: Handler = { match ->
            val token = match.data.token as String
            onScreen(Screen.ResetPassword(token))
        }
        val projectListHandler: Handler = { onScreen(Screen.ProjectList) }
        val editorHandler: Handler = { match ->
            val id = match.data.id as String
            onScreen(Screen.Editor(id, diagramId = null))
        }
        val editorWithDiagramHandler: Handler = { match ->
            val id = match.data.id as String
            val diagramId = match.data.diagramId as String
            onScreen(Screen.Editor(id, diagramId))
        }
        val profileHandler: Handler = { onScreen(Screen.Profile) }
        val impressumHandler: Handler = { onScreen(Screen.Impressum) }
        val datenschutzHandler: Handler = { onScreen(Screen.Datenschutz) }
        val agbHandler: Handler = { onScreen(Screen.Agb) }
        // R-21: the only unauthenticated content route -- must be registered BEFORE notFound()
        // below (registration order matters for Navigo's fallback), so an anonymous visitor never
        // lands on the notFound handler's "/projects" redirect (which resolves to the Login screen).
        val publicShareHandler: Handler = { match ->
            val token = match.data.token as String
            onScreen(Screen.PublicShare(token))
        }
        val notFoundHandler: Handler = { routing.navigate("/projects") }

        routing.on("/login", loginHandler)
        routing.on("/verify-email/:token", verifyEmailHandler)
        routing.on("/reset-password/:token", resetPasswordHandler)
        routing.on("/projects", projectListHandler)
        // R-22: /projects/:id (no diagram segment) resolves the project's default diagram in
        // PortalApp.kt's renderScreen, then replaces the route with the form below.
        routing.on("/projects/:id/diagrams/:diagramId", editorWithDiagramHandler)
        routing.on("/projects/:id", editorHandler)
        routing.on("/profile", profileHandler)
        routing.on("/impressum", impressumHandler)
        routing.on("/datenschutz", datenschutzHandler)
        routing.on("/agb", agbHandler)
        routing.on("/s/:token", publicShareHandler)
        routing.notFound(notFoundHandler)
    }

    public fun navigate(path: String) {
        routing.navigate(path)
    }
}
